import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Container,
  Chip,
  IconButton,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  CircularProgress,
  alpha,
} from '@mui/material';
import { Add as AddIcon, Visibility as VisibilityIcon, SwapHoriz as TransferIcon, Store as StoreIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getTransferHistory } from '../services/inventoryTransferService';
import { getStoresFiltered } from '../../stores/services/storeService';
import ReusableDataGrid from '../../../components/ReusableDataGrid';

function TransferListPage() {
  const navigate = useNavigate();
  const [transfers, setTransfers] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    storeId: '',
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    loadStores();
    loadTransfers();
  }, []);

  const loadStores = async () => {
    try {
      const response = await getStoresFiltered({ includeVirtual: false, includeInactive: false });
      const storesData = response?.payload || response || [];
      setStores(Array.isArray(storesData) ? storesData : []);
    } catch (error) {
    }
  };

  const loadTransfers = async () => {
    try {
      setLoading(true);
      const response = await getTransferHistory(filters);
      const transfersData = response?.payload || response || [];
      
      const mappedTransfers = (Array.isArray(transfersData) ? transfersData : []).map((transfer) => ({
        id: transfer.Id || transfer.id || transfer.TransferId || transfer.transferId,
        transferNumber: transfer.TransferNumber || transfer.transferNumber || 'N/A',
        transferDate: transfer.TransferDate || transfer.transferDate,
        sourceStoreName: transfer.SourceStoreName || transfer.sourceStoreName || 'Unknown',
        destinationStoreName: transfer.DestinationStoreName || transfer.destinationStoreName || 'Unknown',
        totalItems: transfer.TotalItems || transfer.totalItems || 0,
        totalQuantity: transfer.TotalQuantity || transfer.totalQuantity || 0,
        transferReason: transfer.TransferReason || transfer.transferReason || '',
        status: transfer.Status || transfer.status || 'Completed',
      }));

      setTransfers(mappedTransfers);
    } catch (error) {
      toast.error('Failed to load transfer history');
      setTransfers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    loadTransfers();
  };

  const handleClearFilters = () => {
    setFilters({ storeId: '', startDate: '', endDate: '' });
    setTimeout(() => loadTransfers(), 0);
  };

  const columns = [
    {
      field: 'transferNumber',
      headerName: 'Transfer #',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium" color="primary">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'transferDate',
      headerName: 'Date',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString() : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'sourceStoreName',
      headerName: 'From',
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) => (
        <Chip
          icon={<StoreIcon fontSize="small" />}
          label={params.value}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontWeight: 500 }}
        />
      ),
    },
    {
      field: 'destinationStoreName',
      headerName: 'To',
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) => (
        <Chip
          icon={<StoreIcon fontSize="small" />}
          label={params.value}
          size="small"
          color="secondary"
          variant="outlined"
          sx={{ fontWeight: 500 }}
        />
      ),
    },
    {
      field: 'totalItems',
      headerName: 'Items',
      flex: 0.6,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip label={params.value} size="small" color="default" />
      ),
    },
    {
      field: 'totalQuantity',
      headerName: 'Qty',
      flex: 0.6,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'Completed' ? 'success' : 'warning'}
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.6,
      minWidth: 80,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => navigate(`/inventory/transfers/view/${params.row.id}`)}
          sx={{
            color: 'primary.main',
            '&:hover': {
              backgroundColor: alpha('#1976d2', 0.1),
            },
          }}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ background: 'linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%)', minHeight: '100vh', pt: 3, pb: 8 }}>
      <Container maxWidth="xl">
        {/* Header Card */}
        <Card elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden', mb: 3 }}>
          <CardContent
            sx={{
              background: alpha('#1976d2', 0.08),
              borderBottom: `1px solid ${alpha('#1976d2', 0.15)}`,
              p: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ bgcolor: alpha('#1976d2', 0.15), p: 1.5, borderRadius: '12px', mr: 2 }}>
                  <TransferIcon sx={{ color: '#1976d2' }} fontSize="large" />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    Inventory Transfers
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Transfer inventory between stores
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/inventory/transfers/create')}
                sx={{
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  px: 3,
                  py: 1.2,
                  boxShadow: 3,
                }}
              >
                New Transfer
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Filters Card */}
        <Card elevation={2} sx={{ borderRadius: '16px', mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Filters
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Store"
                  value={filters.storeId}
                  onChange={(e) => handleFilterChange('storeId', e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                >
                  <MenuItem value="">All Stores</MenuItem>
                  {stores.map((store) => (
                    <MenuItem key={store.id || store.Id} value={store.id || store.Id}>
                      {store.storeName || store.StoreName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="End Date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleApplyFilters}
                    sx={{ borderRadius: 1.5, textTransform: 'none' }}
                  >
                    Apply
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleClearFilters}
                    sx={{ borderRadius: 1.5, textTransform: 'none' }}
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Transfers Table */}
        <Paper elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <CircularProgress />
            </Box>
          ) : (
            <ReusableDataGrid
              rows={transfers}
              columns={columns}
              loading={loading}
              pageSize={10}
              getRowId={(row) => row.id}
              emptyMessage="No transfers found"
            />
          )}
        </Paper>
      </Container>
    </Box>
  );
}

export default TransferListPage;
